"use client";

import type React from "react";
import { type ReactNode } from "react";
import { MousePointerClick, Plus, SearchX } from "lucide-react";
import { cn } from "@/lib/utils/index";
import { FetchButton } from "@/fetch-ui/FetchButton";

type FetchEmptyStateAction = {
  /** Button label text */
  label: string;
  /** Button variant. Defaults to "outline" (existing behavior). */
  variant?: "outline" | "ghost";
  /** Hide the leading plus icon. Defaults to false (icon shown). */
  hideIcon?: boolean;
} & (
  | {
      /** Click handler for the action button */
      onClick: () => void;
      href?: never;
    }
  | {
      /** Renders the action as a link to this URL instead of a click handler */
      href: string;
      onClick?: never;
    }
);

interface FetchEmptyStateBaseProps {
  /** Primary message, e.g. "No forms yet" */
  title: string;
  /** Secondary message, e.g. "Create a form to get started" */
  subtitle?: string;
  /** Optional second action rendered beside the main one, e.g. a create CTA next to "Clear filters" */
  secondaryAction?: FetchEmptyStateAction;
  /** Optional icon rendered above the title. `filtered` and `none-selected` fall back to their own lucide icon. */
  icon?: ReactNode;
  /** Optional className override for the outer container */
  className?: string;
}

/**
 * Which empty state this is. Each one says what the next step is:
 * - `empty` (default): nothing exists yet; `action` is the create or next-step button.
 * - `filtered`: things exist but the current search or filters hide all of them; the main button
 *   is always "Clear filters", wired to `onClearFilters`.
 * - `none-selected`: a panel waiting on a selection ("Select venues in the table to email them").
 */
type FetchEmptyStateVariantProps =
  | {
      variant?: "empty";
      /** Optional action button rendered below the text */
      action?: FetchEmptyStateAction;
      onClearFilters?: never;
      clearFiltersLabel?: never;
    }
  | {
      variant: "filtered";
      /** Clears the search and filters that hide every item. */
      onClearFilters: () => void;
      /** Label for the clear button. Defaults to "Clear filters"; say "Clear search" when only a search is narrowing. */
      clearFiltersLabel?: string;
      action?: never;
    }
  | {
      variant: "none-selected";
      /** Optional action button rendered below the text */
      action?: FetchEmptyStateAction;
      onClearFilters?: never;
      clearFiltersLabel?: never;
    };

/** Props for {@link FetchEmptyState}. */
export type FetchEmptyStateProps = FetchEmptyStateBaseProps & FetchEmptyStateVariantProps;

/** Default copy for the `filtered` variant's button. */
const CLEAR_FILTERS_LABEL = "Clear filters";

/** The icon each variant shows when the caller passes none. `empty` keeps its old behavior: no icon. */
const DEFAULT_ICON_BY_VARIANT: Record<NonNullable<FetchEmptyStateProps["variant"]>, ReactNode> = {
  empty: null,
  filtered: <SearchX className="size-8 text-gray-300" aria-hidden="true" />,
  "none-selected": <MousePointerClick className="size-8 text-gray-300" aria-hidden="true" />,
};

/**
 * Dashed-border empty state placeholder with optional subtitle and up to two action buttons,
 * centered in its container. Used when a list or grid has no items to display, when filters hide
 * all of them (`variant="filtered"`), or when a panel waits on a selection
 * (`variant="none-selected"`).
 *
 * @param props - See {@link FetchEmptyStateProps}.
 * @returns The empty state placeholder.
 * @throws Never.
 */
export function FetchEmptyState(props: FetchEmptyStateProps): React.ReactElement {
  // Derived values
  const { title, subtitle, secondaryAction, icon, className } = props;
  const variant = props.variant ?? "empty";
  const mainAction: FetchEmptyStateAction | undefined =
    props.variant === "filtered"
      ? { label: props.clearFiltersLabel ?? CLEAR_FILTERS_LABEL, onClick: props.onClearFilters, hideIcon: true }
      : props.action;
  const actions = [mainAction, secondaryAction].filter((item): item is FetchEmptyStateAction => item !== undefined);
  const shownIcon = icon ?? DEFAULT_ICON_BY_VARIANT[variant];

  // Render
  return (
    <div
      data-variant={variant}
      className={cn(
        "flex flex-1 flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 py-16",
        className
      )}
    >
      {shownIcon && <div className="mb-3">{shownIcon}</div>}
      <p className="text-center text-sm font-medium text-gray-700">{title}</p>
      {subtitle && <p className="mt-1 max-w-sm text-center text-xs font-medium text-gray-600">{subtitle}</p>}
      {actions.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          {actions.map((item) => (
            <FetchButton
              key={item.label}
              variant={item.variant ?? "outline"}
              size="sm"
              leftIcon={item.hideIcon ? undefined : <Plus className="h-3.5 w-3.5" />}
              onClick={item.onClick}
              href={item.href}
              className="whitespace-nowrap"
            >
              {item.label}
            </FetchButton>
          ))}
        </div>
      )}
    </div>
  );
}
